import type { components } from '@/types/api/v1'
import { formatters } from '@/utils/intl/dateTimeFormatters'
import { HistoryOutlined } from '@ant-design/icons'
import { Texto } from '@gravitate-js/excalibrr'
import { Alert, Card, Col, Flex, Popover, Row } from 'antd'
import dayjs from 'dayjs'
import type { PropsWithChildren } from 'react'

type Metrics = components['schemas']['DriverScheduleMetrics']
type Metric = components['schemas']['WorkedHoursMetric']
type MetricValue = components['schemas']['LimitMetric'] | null
type MetricType = Metric['type']

//these should total 24
const METRIC_NAME_WIDTH = 10
const SHIFT_WIDTH = 7
const ROLLING_WINDOW_WIDTH = 7

const metricNames: Record<MetricType, string> = {
  drive_time: 'Drive Time',
  on_duty: 'On Duty',
  estimated: 'Estimated',
}

const formatMax = (val: number) => (val !== Math.round(val) ? val.toFixed(1) : val)

const MetricValue = ({ metric }: { metric?: MetricValue }) => {
  if (!metric) return null

  return (
    <Flex style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', width: '100%' }}>
      <Texto category='p2' align='end'>
        {metric.value.toFixed(1)}
      </Texto>
      {metric.max !== null && (
        <Texto category='p1' appearance='hint'>
          /{formatMax(metric.max)}h
        </Texto>
      )}
    </Flex>
  )
}

const MetricRow = ({ metric }: { metric?: Metric | null }) => {
  if (!metric) return null
  return (
    <Row>
      <Col span={METRIC_NAME_WIDTH}>
        <Texto category='p1'>{metricNames[metric.type]}</Texto>
      </Col>
      <Col span={SHIFT_WIDTH}>
        <MetricValue metric={metric.current} />
      </Col>
      <Col span={ROLLING_WINDOW_WIDTH}>
        <MetricValue metric={metric.rolling} />
      </Col>
    </Row>
  )
}

type Utilization = {
  message?: string
  status?: 'optimal' | 'underutilized' | 'close_to_max' | 'overutilized' | 'not_utilized'
}

const UtilizationLine = ({ status, message }: Utilization) => {
  if (!message) return null
  if (status === 'close_to_max' || status === 'underutilized') {
    return (
      <Alert
        message={message}
        type='warning'
        showIcon
        data-testid={status === 'close_to_max' ? 'driver-close-to-max' : 'driver-underutilized'}
      />
    )
  }
  if (status === 'overutilized') {
    return <Alert message={message} type='error' showIcon data-testid='driver-overutilized' />
  }
  return null
}
const MetricsCard = ({ metrics, status, message }: { metrics?: Metrics | null } & Utilization) => {
  const utilization = <UtilizationLine status={status} message={message} />
  if (!metrics) {
    return utilization
  }
  return (
    <Card style={{ minWidth: '300px', width: '100%', whiteSpace: 'nowrap' }} styles={{ body: { padding: '12px' } }}>
      <Row style={{ fontWeight: 'bold' }}>
        <Col span={METRIC_NAME_WIDTH}>
          <Texto category='p2' weight={600}>
            Driver Hours
          </Texto>
        </Col>
        <Col span={SHIFT_WIDTH}>
          <Texto category='p2' align='end' weight={600}>
            Shift
          </Texto>
        </Col>
        <Col span={ROLLING_WINDOW_WIDTH}>
          <Texto category='p2' align='end' weight={600}>
            {metrics.rolling_window_type === '8 day' ? '8 Days' : '7 Days'}
          </Texto>
        </Col>
      </Row>
      <MetricRow metric={metrics.worked_hours.estimated} />
      <MetricRow metric={metrics.worked_hours.on_duty} />
      <MetricRow metric={metrics.worked_hours.drive_time} />
      {(utilization || metrics.updated_on) && (
        <Flex vertical gap={4} className='pt-2'>
          {metrics.updated_on && (
            <Flex gap={4}>
              <HistoryOutlined style={{ color: 'var(--theme-color-2)' }} />
              <Texto category='label' style={{ color: 'var(--theme-color-2)' }}>
                Updated {formatters.MONTH_DATE_TIME(dayjs.tz(metrics.updated_on, 'UTC'))}
              </Texto>
            </Flex>
          )}
          {utilization}
        </Flex>
      )}
    </Card>
  )
}

export const HoursOfServicePopover = ({
  children,
  metrics,
  message,
  status,
}: PropsWithChildren<{ metrics?: components['schemas']['DriverScheduleMetrics'] | null } & Utilization>) => (
  <Popover
    styles={{ body: { padding: '0px' }, root: undefined }}
    content={<MetricsCard metrics={metrics} message={message} status={status} />}
  >
    {children}
  </Popover>
)
